/*
 * Product page touches for the routine products.
 *
 * The order of the buy box belongs to the theme editor: drag the blocks and
 * the page follows. Nothing here rearranges the page.
 *
 * Two things the editor cannot express are done here.
 *
 * The description is turned from a collapsible into a heading with the text
 * open beneath it. The theme's description block only prints a heading when it
 * is set to collapse, and collapsing is exactly what is not wanted - so the
 * heading is kept and the details element it came in is taken away. Nothing is
 * left to open, close, mis-measure its own height or overlap what follows.
 *
 * The eyebrow, the star rating and the price are carried onto narrow screens.
 * The theme builds the top of this page twice over - a desktop column
 * (.main-product__details) and a separate mobile one
 * (.main-wrapper-mobile-block), each hidden from the other by width - and the
 * mobile one only knows how to render a handful of block types. Custom liquid,
 * price and app blocks are not among them, so on a phone all three would be
 * stranded in the desktop column below the gallery, away from the name they
 * belong to. They are moved across while that column is the one on screen, and
 * put back when it is not, so the desktop layout is never touched.
 */

(function () {
  'use strict';

  var DESKTOP = '.main-product__details';
  var MOBILE = '.main-wrapper-mobile-block';

  /* Where a node started, so it can go home when the window widens again. */
  var homes = [];

  function remember(node) {
    if (!node) return;
    var known = homes.some(function (entry) {
      return entry.node === node;
    });
    if (!known) homes.push({ node: node, parent: node.parentNode, next: node.nextSibling });
  }

  /* The block's own wrapper, not the element inside it: the theme gives each
     block a div carrying that block's spacing. */
  function blockOf(node, column) {
    while (node && node.parentNode !== column) node = node.parentNode;
    return node;
  }

  function textBlocks(column) {
    return Array.prototype.filter.call(column.children, function (node) {
      return node.matches && node.matches('p.cstm-tex, p.cstm-text');
    });
  }

  /* The review app prints its own markup straight into the column, under one
     of these names depending on which widget is in use. */
  var STARS = '[data-oke-star-rating], .oke-sr, .okeReviews, .oke-stars';

  function found(selector, desktop, mobile) {
    var node = desktop.querySelector(selector) || (mobile && mobile.querySelector(selector));
    if (!node) return null;
    var column = node.closest(DESKTOP) || node.closest(MOBILE);
    return column ? blockOf(node, column) : null;
  }

  function place() {
    var desktop = document.querySelector(DESKTOP);
    var mobile = document.querySelector(MOBILE);
    if (!desktop) return;

    var eyebrow = found('.routine-eyebrow', desktop, mobile);
    var stars = found(STARS, desktop, mobile);
    var price = found('[id^="price-"]', desktop, mobile);

    /* Named so the stylesheet can level their spacing, which otherwise comes
       from each page's own block settings and the review app's own margins. */
    if (stars) stars.classList.add('routine-stars');
    if (eyebrow) eyebrow.classList.add('routine-eyebrow-block');

    remember(eyebrow);
    remember(stars);
    remember(price);

    if (!mobile || mobile.offsetParent === null) {
      homes.forEach(function (entry) {
        if (entry.node.parentNode !== entry.parent) {
          entry.parent.insertBefore(entry.node, entry.next);
        }
      });
      return;
    }

    /*
     * The mobile column reads eyebrow, stars, name, subtitle, price - the same
     * order as the desktop one. It renders the name itself, so the two above it
     * go before it and the price after the subtitle.
     */
    var title = mobile.querySelector('h1.main-product__heading');

    [eyebrow, stars].forEach(function (node) {
      if (!node || node.parentNode === mobile) return;
      if (title) {
        mobile.insertBefore(node, title);
      } else {
        mobile.insertBefore(node, mobile.firstChild);
      }
    });

    if (price && price.parentNode !== mobile) {
      var texts = textBlocks(mobile);
      var after = texts.length ? texts[texts.length - 1] : title;
      if (after) {
        mobile.insertBefore(price, after.nextSibling);
      } else {
        mobile.appendChild(price);
      }
    }
  }

  /*
   * Give the description a heading and leave its text open beneath it.
   *
   * The block arrives in one of two shapes depending on its Collapse content
   * setting, and both are handled so the product pages read the same whichever
   * way that box is ticked:
   *
   *   ticked   - a collapsible carrying the heading typed in the editor. The
   *              heading is kept and the collapsible taken away, so there is
   *              nothing left to toggle or to mismeasure its own height and
   *              land on top of the accordions below.
   *   unticked - the description text and no heading at all, because the theme
   *              only prints one for a collapsible. A heading is added.
   *
   * Ticking the box is the better of the two, because then the wording of the
   * heading is the editor's rather than this file's.
   *
   * The description is the only .RTE-content the theme puts directly inside a
   * column - the accordions carry one each, but nested - which is what tells
   * it apart from Details, Key Benefits and the rest. Those are left alone.
   */
  var DEFAULT_HEADING = 'Product Description';

  function describe(wrapper) {
    if (wrapper.classList.contains('routine-description')) return;

    var accordion = wrapper.querySelector(':scope > accordion-tab');
    var headText = accordion && accordion.querySelector('.accordion__item__head p');
    var content = accordion ? accordion.querySelector('.accordion__item__body-content') : wrapper;
    if (!content) return;

    var heading = document.createElement('p');
    heading.className = 'routine-description__head';
    heading.textContent = (headText && headText.textContent.trim()) || DEFAULT_HEADING;

    var body = document.createElement('div');
    body.className = 'routine-description__body';
    while (content.firstChild) body.appendChild(content.firstChild);

    if (accordion) accordion.remove();

    wrapper.classList.add('routine-description');

    /* Shown at every width: the stylesheet drops the mobile column's copy
       instead, so the description sits with the other accordions below the
       gallery on a phone rather than above it. */
    wrapper.classList.remove('description-mob');

    wrapper.appendChild(heading);
    wrapper.appendChild(body);
  }

  function openDescription() {
    [DESKTOP, MOBILE].forEach(function (selector) {
      var column = document.querySelector(selector);
      if (!column) return;

      Array.prototype.forEach.call(column.children, function (node) {
        if (node.classList && node.classList.contains('RTE-content')) describe(node);
      });
    });
  }

  function apply() {
    place();
    openDescription();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }

  var resizing = null;
  window.addEventListener('resize', function () {
    if (resizing) clearTimeout(resizing);
    resizing = setTimeout(place, 150);
  });

  /* The theme editor rebuilds the section on every settings change. */
  document.addEventListener('shopify:section:load', function () {
    homes = [];
    apply();
  });
})();
